/**
 * JunctionsHub — sister page to RoundaboutsHub.jsx.
 *
 * Same architecture, same teaching philosophy: recognise the layout,
 * read the clues, decide calmly. 10 visual SVG types in a swipeable
 * carousel + 12 understanding cards ("one thing at a time"), each
 * snoozable for 7 days.
 *
 * Feb 11, 2026 — Phase 1 ship (full scaffold; homework injector is
 * wired in the backend, frontend consumer follows next turn).
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ChevronLeft, ChevronRight, Compass, BookOpen,
  Lightbulb, Eye, Target, Sparkles, Award, Lock,
} from "lucide-react";
import JunctionDiagram from "../components/learner/JunctionDiagram";
import CalmEmptyState from "../components/learner/CalmEmptyState";
import RelatedHubsFooter from "../components/learner/RelatedHubsFooter";

const API = process.env.REACT_APP_BACKEND_URL;


export default function JunctionsHub() {
  const nav = useNavigate();
  const [types, setTypes] = useState([]);
  const [cards, setCards] = useState([]);
  const [badges, setBadges] = useState([]);
  const [viewedCount, setViewedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [openCardId, setOpenCardId] = useState(null);
  const carouselRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [tRes, cRes, bRes] = await Promise.all([
          axios.get(`${API}/api/junctions/types`),
          axios.get(`${API}/api/junctions/cards`),
          axios.get(`${API}/api/junctions/me/badges`,
            { withCredentials: true }).catch(() => ({ data: { badges: [], next: [], viewed_count: 0 } })),
        ]);
        setTypes(tRes.data?.types || []);
        setCards(cRes.data?.cards || []);
        setViewedCount(bRes.data?.viewed_count || 0);
        // Stitch earned + locked together so the row reads as a
        // progress map, not just "trophies collected".
        const earnedIds = new Set((bRes.data?.badges || []).map((x) => x.badge_id));
        const locked = (bRes.data?.next || []).map((x) => ({
          ...x, earned: false,
        }));
        const earned = (bRes.data?.badges || []).map((x) => ({
          id: x.badge_id, label: x.label, tagline: x.tagline, earned: true,
        }));
        setBadges([...earned, ...locked]);
      } catch (e) {
        toast.error("Couldn't load Junctions Hub. Try again.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function markTypeViewed(typeId) {
    try {
      const { data } = await axios.post(
        `${API}/api/junctions/me/type-viewed`,
        { type_id: typeId },
        { withCredentials: true },
      );
      setViewedCount(data?.viewed_count || 0);
      (data?.newly_earned || []).forEach((b) => {
        toast.success(`Badge earned: ${b.label}`, { description: b.tagline });
      });
    } catch (_) {
      /* silent — viewing shouldn't error noisily */
    }
  }

  const scrollCarousel = (dir) => {
    const el = carouselRef.current;
    if (!el) return;
    const card = el.querySelector("[data-carousel-item]");
    const cardW = card?.offsetWidth || 280;
    el.scrollBy({ left: dir * (cardW + 12), behavior: "smooth" });
  };

  return (
    <div
      className="bg-slate-50 dark:bg-slate-950 pb-12"
      style={{ minHeight: "100dvh" }}
      data-testid="junctions-hub-page"
    >
      <header className="sticky top-0 z-30 bg-white/95 dark:bg-slate-900/95 backdrop-blur border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            data-testid="junctions-back"
            onClick={() => nav(-1)}
            className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <ArrowLeft className="w-5 h-5 text-slate-500" />
          </button>
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-indigo-600 dark:text-indigo-400">
              Trigger Learning
            </p>
            <h1 className="text-lg font-extrabold text-slate-900 dark:text-white tracking-tight">
              Junctions
            </h1>
          </div>
          <div
            data-testid="junctions-progress-pill"
            className="text-[11px] font-extrabold uppercase tracking-widest px-2 py-1 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-200 border border-indigo-200 dark:border-indigo-800/40"
          >
            {viewedCount}/{types.length || 10}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 pt-5 space-y-8">
        {/* Intro */}
        <section className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 p-5">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-indigo-600 dark:text-indigo-400 mb-1.5">
            Why this hub
          </p>
          <h2 className="text-xl font-extrabold text-slate-900 dark:text-white tracking-tight mb-1.5">
            Read the road. Spot the clue. Commit calmly.
          </h2>
          <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
            Junctions feel scary when you don't know what to look for. This
            hub teaches you to <em>see</em> the layout, read the markings
            and other drivers, and decide — one understanding at a time.
          </p>
        </section>

        {/* SECTION 1: Swipeable types carousel */}
        <section data-testid="junction-types-carousel">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-slate-900 dark:text-white inline-flex items-center gap-1.5">
              <Compass className="w-4 h-4 text-indigo-600" />
              Spot the layout
            </h3>
            <div className="flex items-center gap-1.5">
              <button
                data-testid="junction-carousel-prev"
                onClick={() => scrollCarousel(-1)}
                className="w-8 h-8 rounded-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex items-center justify-center hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                <ChevronLeft className="w-4 h-4 text-slate-500" />
              </button>
              <button
                data-testid="junction-carousel-next"
                onClick={() => scrollCarousel(1)}
                className="w-8 h-8 rounded-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex items-center justify-center hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                <ChevronRight className="w-4 h-4 text-slate-500" />
              </button>
            </div>
          </div>

          <div
            ref={carouselRef}
            className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 -mx-4 px-4"
            style={{ scrollbarWidth: "none" }}
          >
            {loading && Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                data-carousel-item
                className="flex-shrink-0 w-64 h-72 rounded-2xl bg-slate-200/50 dark:bg-slate-800/50 animate-pulse"
              />
            ))}
            {!loading && types.length === 0 && (
              <div className="flex-shrink-0 w-full">
                <CalmEmptyState
                  testid="junctions-types-empty"
                  tone="nothing-yet"
                  title="Junction layouts loading soon"
                  body="The visual library is being prepared — check back in a moment."
                />
              </div>
            )}
            {!loading && types.map((t) => (
              <article
                key={t.id}
                data-carousel-item
                data-testid={`junction-type-${t.id}`}
                onClick={() => markTypeViewed(t.id)}
                className="snap-start flex-shrink-0 w-64 sm:w-72 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden cursor-pointer"
              >
                <div className="aspect-square bg-slate-900 p-3">
                  <JunctionDiagram id={t.svg} className="w-full h-full" />
                </div>
                <div className="p-4 space-y-2">
                  <h4 className="text-base font-extrabold text-slate-900 dark:text-white leading-tight">
                    {t.label}
                  </h4>
                  <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                    {t.blurb}
                  </p>
                  <ul className="space-y-1 pt-1">
                    {(t.key_features || []).map((f, i) => (
                      <li
                        key={i}
                        className="text-[11px] text-slate-500 dark:text-slate-400 flex items-start gap-1.5"
                      >
                        <span className="mt-1 w-1 h-1 rounded-full bg-indigo-500 flex-shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  {t.common_mistake && (
                    <div className="mt-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40 rounded-xl px-3 py-2">
                      <p className="text-[10px] uppercase tracking-widest font-bold text-amber-700 dark:text-amber-400 mb-0.5">
                        Common mistake
                      </p>
                      <p className="text-[11px] text-amber-900 dark:text-amber-200 leading-snug">
                        {t.common_mistake}
                      </p>
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>
          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-2 text-center">
            Swipe through the layouts · {types.length || 10} types
          </p>
        </section>

        {/* Badges row */}
        {badges.length > 0 && (
          <section data-testid="junctions-badges-row">
            <h3 className="text-sm font-bold text-slate-900 dark:text-white inline-flex items-center gap-1.5 mb-3">
              <Award className="w-4 h-4 text-amber-600" />
              Earned along the way
              <span className="ml-1 text-[10px] text-slate-500 dark:text-slate-400 font-semibold uppercase tracking-wide">
                {badges.filter(b => b.earned).length} of {badges.length}
              </span>
            </h3>
            <div className="grid grid-cols-3 gap-2.5">
              {badges.map((b) => (
                <div
                  key={b.id}
                  data-testid={`junctions-badge-${b.id}`}
                  className={`rounded-2xl border px-3 py-3 transition-colors ${
                    b.earned
                      ? "border-amber-300 dark:border-amber-700 bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-900/10"
                      : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 opacity-70"
                  }`}
                >
                  <div className="flex items-center gap-1.5 mb-1.5">
                    {b.earned ? (
                      <Award className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                    ) : (
                      <Lock className="w-3.5 h-3.5 text-slate-400" />
                    )}
                    <p className="text-[10px] uppercase tracking-widest font-extrabold text-slate-700 dark:text-slate-200">
                      {b.label}
                    </p>
                  </div>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400 leading-snug">
                    {b.tagline || (b.threshold ? `View ${b.threshold} types` : "")}
                  </p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* SECTION 2: Understanding cards */}
        <section data-testid="junctions-understanding-cards">
          <h3 className="text-sm font-bold text-slate-900 dark:text-white inline-flex items-center gap-1.5 mb-3">
            <BookOpen className="w-4 h-4 text-indigo-600" />
            One thing at a time
          </h3>
          <p className="text-xs text-slate-600 dark:text-slate-400 mb-3">
            Each card teaches one decision. Tap to expand the reasoning,
            then tap "Got it" to snooze it for 7 days.
          </p>
          <div className="space-y-2.5">
            {loading && Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="h-16 rounded-2xl bg-slate-200/50 dark:bg-slate-800/50 animate-pulse"
              />
            ))}
            {!loading && cards.length === 0 && (
              <CalmEmptyState
                testid="junctions-cards-empty"
                tone="nothing-yet"
                title="Understanding cards coming up"
                body="Once they're seeded, you'll see one teaching point at a time here."
              />
            )}
            {!loading && cards.map((c) => (
              <JunctionUnderstandingRow
                key={c.id}
                card={c}
                open={openCardId === c.id}
                onToggle={() =>
                  setOpenCardId(openCardId === c.id ? null : c.id)
                }
              />
            ))}
          </div>
        </section>

        <RelatedHubsFooter currentPath="/junctions" />
      </main>
    </div>
  );
}


// ──────────────────────────────────────────────────────────────────
// Understanding card row — uses the same 6-point structure as
// the roundabouts equivalent. (Kept inline rather than imported to
// avoid coupling the two systems.)
// ──────────────────────────────────────────────────────────────────

const TONE_BG = {
  emerald: "bg-emerald-50 dark:bg-emerald-900/15 border-emerald-200 dark:border-emerald-900/40 text-emerald-900 dark:text-emerald-200",
  blue:    "bg-blue-50 dark:bg-blue-900/15 border-blue-200 dark:border-blue-900/40 text-blue-900 dark:text-blue-200",
  amber:   "bg-amber-50 dark:bg-amber-900/15 border-amber-200 dark:border-amber-900/40 text-amber-900 dark:text-amber-200",
  indigo:  "bg-indigo-50 dark:bg-indigo-900/15 border-indigo-200 dark:border-indigo-900/40 text-indigo-900 dark:text-indigo-200",
  rose:    "bg-rose-50 dark:bg-rose-900/15 border-rose-200 dark:border-rose-900/40 text-rose-900 dark:text-rose-200",
};

function CardFact({ icon: Icon, label, body, tone = "blue" }) {
  return (
    <div className={`rounded-xl border px-3 py-2.5 ${TONE_BG[tone]}`}>
      <p className="text-[10px] uppercase tracking-widest font-extrabold flex items-center gap-1.5 mb-0.5">
        <Icon className="w-3 h-3" />
        {label}
      </p>
      <p className="text-[12px] leading-snug">{body}</p>
    </div>
  );
}

function JunctionUnderstandingRow({ card, open, onToggle }) {
  const [busy, setBusy] = useState(false);

  async function markUnderstood() {
    setBusy(true);
    try {
      await axios.post(
        `${API}/api/junctions/me/understood`,
        { card_id: card.id },
        { withCredentials: true },
      );
      toast.success("Got it — snoozed for 7 days.");
    } catch {
      toast.error("Couldn't save. Try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      data-testid={`junctions-understanding-${card.id}`}
      className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden"
    >
      <button
        type="button"
        data-testid={`junctions-understanding-toggle-${card.id}`}
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
      >
        <div className="w-9 h-9 rounded-xl bg-indigo-500/10 border border-indigo-500/30 flex items-center justify-center flex-shrink-0">
          <Lightbulb className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-slate-900 dark:text-white leading-snug">
            {card.title}
          </p>
        </div>
        <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>

      {open && (
        <div className="px-4 pb-4 pt-1 space-y-3 border-t border-slate-100 dark:border-slate-800">
          <CardFact icon={Sparkles}     label="Why this matters"      body={card.why}       tone="emerald" />
          <CardFact icon={Eye}          label="What to look for"      body={card.look_for}  tone="blue" />
          <CardFact icon={Target}       label="The cue"               body={card.cue}       tone="amber" />
          <CardFact icon={ChevronRight} label="What to do"            body={card.action}    tone="indigo" />
          <CardFact icon={Lightbulb}    label="Why it works"          body={card.why_works} tone="emerald" />
          <CardFact icon={Sparkles}     label="What success looks like" body={card.success} tone="rose" />

          <button
            type="button"
            data-testid={`junctions-got-it-${card.id}`}
            onClick={markUnderstood}
            disabled={busy}
            className="w-full mt-2 inline-flex items-center justify-center gap-2 h-12 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold disabled:bg-indigo-400"
          >
            {busy ? "Saving…" : "Got it — snooze 7 days"}
          </button>
        </div>
      )}
    </div>
  );
}
